import { create } from 'zustand';
import { supabase, db } from '../lib/supabase';
import type { Project, UserType, BillData, LocationData, SolarDesign, FinancialData, FinancingOption, SiteVisit, EnergyConsumptionData } from '../types';
import { useSubscriptionStore } from './subscriptionStore';
import { getEffectiveConsumption } from '../utils/consumptionNormalization';

interface ProjectState {
  projects: Project[];
  currentProject: Project | null;
  isLoading: boolean;
  error: string | null;
  
  userType: UserType | null;
  billData: BillData | null;
  locationData: LocationData | null;
  solarDesign: SolarDesign | null;
  financialData: FinancialData | null;
  financingOption: FinancingOption | null;
  energyConsumptionData: EnergyConsumptionData | null;
  siteOperationsData: any | null;
  projectId: string | null;
  
  setUserType: (type: UserType) => void;
  setBillData: (data: BillData) => void;
  setLocationData: (data: LocationData) => void;
  setSolarDesign: (design: SolarDesign) => void;
  setFinancialData: (data: FinancialData) => void;
  setFinancingOption: (option: FinancingOption) => void;
  setEnergyConsumptionData: (data: EnergyConsumptionData) => void;
  setSiteOperationsData: (data: any) => void;
  setProjectId: (id: string) => void;
  
  createProject: (userId: string, siteVisit?: SiteVisit) => Promise<Project | null>;
  getProjects: (userId: string) => Promise<Project[]>;
  scheduleSiteVisit: (projectId: string, date: string, time: string) => Promise<boolean>;
  saveEnergyProfile: (projectId: string, userId: string, energyProfileData: {
    objectives: string[];
    energyMix: { [key: string]: number };
  }) => Promise<boolean>;
  updateProject: (projectId: string, userId: string, updateData: {
    location_data?: any;
    solar_design?: any;
    bill_data?: any;
    files?: any;
    location_photos?: string[];
  }) => Promise<boolean>;
  clearProjectData: () => void;
  clearError: () => void;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  currentProject: null,
  isLoading: false,
  error: null,
  
  userType: null,
  billData: null,
  locationData: null,
  solarDesign: null,
  financialData: null,
  financingOption: null,
  energyConsumptionData: null,
  siteOperationsData: null,
  projectId: null,
  
  setUserType: (type) => set({ userType: type }),
  setBillData: (data) => set({ billData: data }),
  setLocationData: (data) => set({ locationData: data }),
  setSolarDesign: (design) => set({ solarDesign: design }),
  setFinancialData: (data) => set({ financialData: data }),
  setFinancingOption: (option) => set({ financingOption: option }),
  setEnergyConsumptionData: (data) => set({ energyConsumptionData: data }),
  setSiteOperationsData: (data) => set({ siteOperationsData: data }),
  setProjectId: (id) => set({ projectId: id }),
  
  createProject: async (userId, siteVisit) => {
    const { userType, billData, locationData, solarDesign, financialData, financingOption, projectId } = get();
    
    if (!userType || !billData || !locationData || !solarDesign || !financialData) {
      set({ error: 'Incomplete project data' });
      return null;
    }

    // Check project limits
    const canCreate = await useSubscriptionStore.getState().checkProjectLimit(userId);
    if (!canCreate) {
      set({ error: 'Project limit reached for your subscription tier' });
      return null;
    }

    // Check system size limit
    const withinLimit = useSubscriptionStore.getState().checkSystemSizeLimit(solarDesign.systemSize);
    if (!withinLimit) {
      set({ error: 'System size exceeds your subscription tier limit' });
      return null;
    }
    
    set({ isLoading: true, error: null });
    
    try {
      
      // Use existing project ID if available, otherwise generate a new one
      const finalProjectId = projectId || crypto.randomUUID();

      const hasMonthlyConsumptionArray =
        billData.monthlyConsumptionArray &&
        billData.monthlyConsumptionArray.length === 12 &&
        billData.monthlyConsumptionArray.some(v => v > 0);

      const yearlyFromMonthly = hasMonthlyConsumptionArray
        ? billData.monthlyConsumptionArray!.reduce((sum, v) => sum + (Number.isFinite(v) ? v : 0), 0)
        : undefined;

      const monthlyUsageFromMonthly = yearlyFromMonthly ? yearlyFromMonthly / 12 : undefined;
      const effectiveConsumption = getEffectiveConsumption(billData, 0);
      
      const projectData = {
        id: finalProjectId,
        user_id: userId,
        user_type: userType,
        project_status: 'pending',
        bill_data: {
          type: billData.type,
          monthly_usage: monthlyUsageFromMonthly ?? billData.monthlyUsage ?? 0,
          monthly_bill: billData.monthlyBill ?? 0,
          custom_tariff_sar_per_kwh:
            typeof billData.customTariffSarPerKwh === 'number' &&
            Number.isFinite(billData.customTariffSarPerKwh) &&
            billData.customTariffSarPerKwh > 0
              ? billData.customTariffSarPerKwh
              : undefined,
          utility_provider: billData.utilityProvider ?? '',
          morning_consumption_pct: effectiveConsumption.morningConsumptionPct,
          effective_yearly_consumption: effectiveConsumption.effectiveYearlyConsumption,
          yearly_consumption:
            yearlyFromMonthly ?? billData.yearlyConsumption ?? (billData.monthlyUsage ? billData.monthlyUsage * 12 : 0)
        },
        location_data: {
          address: locationData.address,
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          area_sq_meters: locationData.areaInSqMeters,
          tilt: locationData.tilt,
          azimuth: locationData.azimuth,
          mounting_type: locationData.mountingType,
          surface_type: locationData.surfaceType
        },
        solar_design: {
          system_size: solarDesign.systemSize,
          panel_count: solarDesign.panelCount,
          yearly_production: solarDesign.yearlyProduction,
          offset: solarDesign.offset,
          financials: {
            total_cost: financialData.totalCost,
            federal_tax_credit: financialData.federalTaxCredit,
            state_tax_credit: financialData.stateTaxCredit,
            monthly_payment: financialData.monthlyPayment,
            payback_period: financialData.paybackPeriod,
            roi: financialData.roi,
            savings_year_1: financialData.savingsYear1,
            savings_25_year: financialData.savings25Year
          }
        },
        financing_option: financingOption,
        location_photos: billData.locationPhotos || [],
        files: {
          ...(billData.uploadedBillUrl && { electricity_bill: billData.uploadedBillUrl }),
          ...(billData.blueprintUrl && { building_blueprint: billData.blueprintUrl }),
          ...(financingOption?.files || {})
        },
        site_visit: siteVisit || null
      };
      
      
      // Check if project already exists
      const { data: existingProject } = await supabase
        .from('projects')
        .select('id')
        .eq('id', finalProjectId)
        .single();
      
      let data;
      if (existingProject) {
        // Update existing project
        const { data: updatedData, error: updateError } = await supabase
          .from('projects')
          .update(projectData)
          .eq('id', finalProjectId)
          .select()
          .single();
          
        if (updateError) {
          console.error("Error updating project:", updateError);
          throw updateError;
        }
        
        data = updatedData;
      } else {
        // Create new project
        const { data: newData, error: insertError } = await supabase
          .from('projects')
          .insert(projectData)
          .select()
          .single();
          
        if (insertError) {
          console.error("Error creating project:", insertError);
          throw insertError;
        }
        
        data = newData;
      }


      // Create notification for new project
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          type: 'success',
          message: `New ${userType} solar project created successfully!`,
          read: false
        });

      if (notificationError) {
        console.error('Error creating notification:', notificationError);
      }
      
      const newProject: Project = {
        id: data.id,
        status: data.project_status,
        type: data.user_type,
        createdAt: data.created_at,
        design: {
          systemSize: data.solar_design.system_size,
          panelCount: data.solar_design.panel_count,
          yearlyProduction: data.solar_design.yearly_production,
          offset: data.solar_design.offset
        },
        location: {
          // Handle new multi-location structure vs legacy single-location
          ...(data.location_data.locations && data.location_data.locations.length > 0 ? {
            // New multi-location structure
            locations: data.location_data.locations,
            total_area_sqm: data.location_data.total_area_sqm,
            photos: data.location_data.photos,
            // Legacy fields for backward compatibility
            address: data.location_data.locations[0]?.address || '',
            latitude: data.location_data.locations[0]?.latitude || 24.7136,
            longitude: data.location_data.locations[0]?.longitude || 46.6753,
            areaInSqMeters: data.location_data.total_area_sqm || 0,
            tilt: data.location_data.locations[0]?.tilt || 10,
            azimuth: data.location_data.locations[0]?.azimuth || 180,
            mountingType: data.location_data.locations[0]?.mounting_type || 'roof-fixed',
            surfaceType: data.location_data.locations[0]?.surface_type || 'asphalt'
          } : {
            // Legacy single-location structure
            locations: [],
            total_area_sqm: data.location_data.area_sq_meters || 0,
            address: data.location_data.address || '',
            latitude: data.location_data.latitude || 24.7136,
            longitude: data.location_data.longitude || 46.6753,
            areaInSqMeters: data.location_data.area_sq_meters || 0,
            tilt: data.location_data.tilt || 10,
            azimuth: data.location_data.azimuth || 180,
            mountingType: data.location_data.mounting_type || 'roof-fixed',
            surfaceType: data.location_data.surface_type || 'asphalt'
          })
        },
        financials: {
          totalCost: data.solar_design.financials.total_cost,
          federalTaxCredit: data.solar_design.financials.federal_tax_credit,
          stateTaxCredit: data.solar_design.financials.state_tax_credit,
          monthlyPayment: data.solar_design.financials.monthly_payment,
          paybackPeriod: data.solar_design.financials.payback_period,
          roi: data.solar_design.financials.roi,
          savingsYear1: data.solar_design.financials.savings_year_1,
          savings25Year: data.solar_design.financials.savings_25_year
        },
        financing: data.financing_option,
        siteVisit: data.site_visit
      };
      
      set(state => ({
        projects: [...state.projects, newProject],
        currentProject: newProject,
        projectId: finalProjectId // Store the project ID for future use
      }));
      
      return newProject;
    } catch (error: any) {
      console.error("Error in createProject:", error);
      set({ error: error?.message || 'Failed to create project' });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },
  
  getProjects: async (userId) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', userId);
      
      if (error) throw error;
      
      if (!data) {
        return [];
      }
      
      const formattedProjects: Project[] = data.map(project => ({
        id: project.id,
        status: project.project_status,
        type: project.user_type,
        createdAt: project.created_at,
        design: project.solar_design ? {
          systemSize: project.solar_design.system_size,
          panelCount: project.solar_design.panel_count,
          yearlyProduction: project.solar_design.yearly_production,
          offset: project.solar_design.offset
        } : {
          systemSize: 0,
          panelCount: 0,
          yearlyProduction: 0,
          offset: 0
        },
        location: project.location_data ? {
          // Handle new multi-location structure vs legacy single-location
          ...(project.location_data.locations && project.location_data.locations.length > 0 ? {
            // New multi-location structure
            locations: project.location_data.locations,
            total_area_sqm: project.location_data.total_area_sqm,
            photos: project.location_data.photos,
            // Legacy fields for backward compatibility
            address: project.location_data.locations[0]?.address || '',
            latitude: project.location_data.locations[0]?.latitude || 24.7136,
            longitude: project.location_data.locations[0]?.longitude || 46.6753,
            areaInSqMeters: project.location_data.total_area_sqm || 0,
            tilt: project.location_data.locations[0]?.tilt || 10,
            azimuth: project.location_data.locations[0]?.azimuth || 180,
            mountingType: project.location_data.locations[0]?.mounting_type || 'roof-fixed',
            surfaceType: project.location_data.locations[0]?.surface_type || 'asphalt'
          } : {
            // Legacy single-location structure
            locations: [],
            total_area_sqm: project.location_data.area_sq_meters || 0,
            address: project.location_data.address || '',
            latitude: project.location_data.latitude || 24.7136,
            longitude: project.location_data.longitude || 46.6753,
            areaInSqMeters: project.location_data.area_sq_meters || 0,
            tilt: project.location_data.tilt || 10,
            azimuth: project.location_data.azimuth || 180,
            mountingType: project.location_data.mounting_type || 'roof-fixed',
            surfaceType: project.location_data.surface_type || 'asphalt'
          })
        } : {
          // Default empty structure
          locations: [],
          total_area_sqm: 0,
          address: '',
          latitude: 0,
          longitude: 0,
          areaInSqMeters: 0,
          tilt: 0,
          azimuth: 0,
          mountingType: 'roof-fixed',
          surfaceType: 'asphalt'
        },
        financials: project.solar_design?.financials ? {
          totalCost: project.solar_design.financials.total_cost,
          federalTaxCredit: project.solar_design.financials.federal_tax_credit,
          stateTaxCredit: project.solar_design.financials.state_tax_credit,
          monthlyPayment: project.solar_design.financials.monthly_payment,
          paybackPeriod: project.solar_design.financials.payback_period,
          roi: project.solar_design.financials.roi,
          savingsYear1: project.solar_design.financials.savings_year_1,
          savings25Year: project.solar_design.financials.savings_25_year
        } : {
          totalCost: 0,
          federalTaxCredit: 0,
          stateTaxCredit: 0,
          monthlyPayment: 0,
          paybackPeriod: 0,
          roi: 0,
          savingsYear1: 0,
          savings25Year: 0
        },
        siteVisit: project.site_visit ? {
          date: project.site_visit.date,
          time: project.site_visit.time,
          status: project.site_visit.status as 'scheduled' | 'completed' | 'cancelled'
        } : undefined,
        financing: project.financing_option
      }));
      
      set({ projects: formattedProjects });
      return formattedProjects;
    } catch (error: any) {
      set({ error: error?.message || 'Failed to fetch projects' });
      return [];
    } finally {
      set({ isLoading: false });
    }
  },
  
  
  scheduleSiteVisit: async (projectId, date, time) => {
    if (get().isLoading) return false;
    
    set({ isLoading: true, error: null });
    
    try {
      const siteVisitData = {
        date,
        time,
        status: 'scheduled'
      };

      // Update the projects table with site_visit JSONB data and mark as completed
      const { data, error } = await supabase
        .from('projects')
        .update({
          site_visit: siteVisitData,
          project_status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', projectId)
        .select()
        .single();
      
      if (error) {
        console.error("Error updating project with site visit:", error);
        throw error;
      }

      if (!data) {
        throw new Error('No data returned from update');
      }

      // Create notification for site visit scheduled
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { error: notificationError } = await supabase
          .from('notifications')
          .insert({
            user_id: user.id,
            type: 'info',
            message: `Site visit scheduled for ${new Date(date).toLocaleDateString()} at ${time}`,
            read: false
          });

        if (notificationError) {
          console.error('Error creating notification:', notificationError);
        }
      }

      return true;
    } catch (error: any) {
      console.error('Error scheduling site visit:', error);
      set({ 
        error: error?.message || 'Unable to schedule the site visit at this time. Please try again or select a different time slot.' 
      });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },
  
  saveEnergyProfile: async (projectId, userId, energyProfileData) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await db.upsertProjectEnergyProfile(
        projectId,
        userId,
        energyProfileData
      );
      
      if (error) {
        throw error;
      }
      
      if (!data) {
        throw new Error('No data returned from energy profile save');
      }
      
      // Store the project ID for future use
      set({ projectId: projectId });
      
      return true;
    } catch (error: any) {
      console.error('Error saving energy profile:', error);
      set({ error: error?.message || 'Failed to save energy profile' });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  updateProject: async (projectId, userId, updateData) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await db.updateProjectData(
        projectId,
        userId,
        updateData
      );
      
      if (error) {
        throw error;
      }
      
      if (!data) {
        throw new Error('No data returned from project update');
      }
      
      return true;
    } catch (error: any) {
      console.error('Error updating project:', error);
      set({ error: error?.message || 'Failed to update project' });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },
  
  clearProjectData: () => set({
    userType: null,
    billData: null,
    locationData: null,
    solarDesign: null,
    financialData: null,
    financingOption: null,
    currentProject: null,
    projectId: null
  }),
  
  clearError: () => set({ error: null })
}));
